import { useEffect } from 'react';
import { validate as uuidValidate } from 'uuid';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApplicationFormPageEnum } from './types';
import { Divider } from '@components/common/Divider';
import {
  addRouteToApplicationRoutes,
  getLasestApplicationProgressRoute,
  parseApplicationRoutesAndSaveToStorage,
  removeRoutesFromApplicationRoutes,
} from '@utils/localStorage';
import { AdditionalNotes } from '@pages/ApplicationFormPage/AdditionalNotes/AdditionalNotes';
import { AddressWrapper } from '@pages/ApplicationFormPage/AddressWrapper/AddressWrapper';
import { ApplicationType } from '@pages/ApplicationFormPage/ApplicationType/ApplicationType';
import { BenefitsAndCredits } from '@pages/ApplicationFormPage/BenefitsAndCredits/BenefitsAndCredits';
import { BonusAndCommision } from '@pages/ApplicationFormPage/BonusAndCommision/BonusAndCommision';
import { CardsWrapper } from '@pages/ApplicationFormPage/CardsWrapper/CardsWrapper';
import { ContactDetails } from '@pages/ApplicationFormPage/ContactDetails/ContactDetails';
import { Criteria } from '@pages/ApplicationFormPage/Criteria/Criteria';
import { Dependants } from '@pages/ApplicationFormPage/Dependants/Dependants';
import { Employment } from '@pages/ApplicationFormPage/Employment/Employment';
import { EmploymentTenure } from '@pages/ApplicationFormPage/EmploymentTenure/EmploymentTenure';
import { FullName } from '@pages/ApplicationFormPage/FullName/FullName';
import { HouseholdOutgoings } from '@pages/ApplicationFormPage/HouseholdOutgoings/HouseholdOutgoings';
import { HouseholdSpendings } from '@pages/ApplicationFormPage/HouseholdSpendings/HouseholdSpendings';
import { Income } from '@pages/ApplicationFormPage/Income/Income';
import { IndustryAndRole } from '@pages/ApplicationFormPage/IndustryAndRole/IndustryAndRole';
import { InReview } from '@pages/ApplicationFormPage/InReview/InReview';
import { IntermediaryFees } from '@pages/ApplicationFormPage/IntermediaryFees/IntermediaryFees';
import { Introducer } from '@pages/ApplicationFormPage/Introducer/Introducer';
import { Introduction } from '@pages/ApplicationFormPage/Introduction/Introduction';
import { LoanRepayments } from '@pages/ApplicationFormPage/LoanRepayments/LoanRepayments';
import { OtherIncome } from '@pages/ApplicationFormPage/OtherIncome/OtherIncome';
import { PartnerAddressHistory } from '@pages/ApplicationFormPage/PartnerAddressHistory/PartnerAddressHistory';
import { PersonalDetails } from '@pages/ApplicationFormPage/PersonalDetails/PersonalDetails';
import { PreviousName } from '@pages/ApplicationFormPage/PreviousName/PreviousName';
import { Purchase } from '@pages/ApplicationFormPage/Purchase/Purchase';
import { Refinancing } from '@pages/ApplicationFormPage/Refinancing/Refinancing';
import { Region } from '@pages/ApplicationFormPage/Region/Region';
import { Relationship } from '@pages/ApplicationFormPage/Relationship/Relationship';
import { Residency } from '@pages/ApplicationFormPage/Residency/Residency';
import { ReviewAndSubmit } from '@pages/ApplicationFormPage/ReviewAndSubmit/ReviewAndSubmit';
import { SecondJob } from '@pages/ApplicationFormPage/SecondJob/SecondJob';
import { StudentLoansWrapper } from '@pages/ApplicationFormPage/StudentLoansWrapper/StudentLoansWrapper';
import { Submission } from '@pages/ApplicationFormPage/Submission/Submission';
import { TravelExpenses } from '@pages/ApplicationFormPage/TravelExpenses/TravelExpenses';
import { PageURL } from '@router/pageURL';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
export const ApplicationFormPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formApplicantNames = searchParams.get('applicantNames')?.split(',');
  const activeApplicant = parseInt(searchParams.get('activeApplicant') || '');
  const activeApplicantName = formApplicantNames?.[activeApplicant - 1] || '';
  const { id = '' } = useParams();
  const formSection = searchParams.get('section') as ApplicationFormPageEnum;
  const { data: applicationInProgress } = useApplicationInProgressById(id);

  useEffect(() => {
    const latestRoute = getLasestApplicationProgressRoute();

    navigate(latestRoute);

    return removeRoutesFromApplicationRoutes;
  }, []);

  useEffect(() => {
    if (applicationInProgress) parseApplicationRoutesAndSaveToStorage(applicationInProgress);

    const latestRoute = getLasestApplicationProgressRoute();

    if (!uuidValidate(id) || !latestRoute) navigate(PageURL.APPLICATIONS);
    if (!formSection || !ApplicationFormPageEnum[formSection]) navigate(latestRoute);

    addRouteToApplicationRoutes();
  }, [id, formSection, applicationInProgress]);

  const ActiveFormPage = () => {
    switch (formSection) {
      case ApplicationFormPageEnum.CRITERIA:
        return <Criteria />;
      case ApplicationFormPageEnum.SUBMISSION:
        return <Submission />;
      case ApplicationFormPageEnum.INTERMEDIARY_FEES:
        return <IntermediaryFees />;
      case ApplicationFormPageEnum.INTRODUCER:
        return <Introducer />;
      case ApplicationFormPageEnum.APPLICATION_TYPE:
        return <ApplicationType />;
      case ApplicationFormPageEnum.RELATIONSHIP:
        return <Relationship />;
      case ApplicationFormPageEnum.FULL_NAME:
        return <FullName />;
      case ApplicationFormPageEnum.PREVIOUS_NAME:
        return <PreviousName />;
      case ApplicationFormPageEnum.PERSONAL_DETAILS:
        return <PersonalDetails />;
      case ApplicationFormPageEnum.CONTACT_DETAILS:
        return <ContactDetails />;
      case ApplicationFormPageEnum.RESIDENCY:
        return <Residency />;
      case ApplicationFormPageEnum.ADDRESS_HISTORY:
      case ApplicationFormPageEnum.ADD_ADDRESS:
        return <AddressWrapper />;
      case ApplicationFormPageEnum.PARTNER_ADDRESS_HISTORY:
        return <PartnerAddressHistory />;
      case ApplicationFormPageEnum.INTRODUCTION:
        return <Introduction />;
      case ApplicationFormPageEnum.PURCHASE:
        return <Purchase />;
      case ApplicationFormPageEnum.REFINANCING:
        return <Refinancing />;
      case ApplicationFormPageEnum.REGION:
        return <Region />;
      case ApplicationFormPageEnum.EMPLOYMENT:
        return <Employment />;
      case ApplicationFormPageEnum.INDUSTRY_AND_ROLE:
        return <IndustryAndRole />;
      case ApplicationFormPageEnum.EMPLOYMENT_TENURE:
        return <EmploymentTenure />;
      case ApplicationFormPageEnum.INCOME:
        return <Income />;
      case ApplicationFormPageEnum.BONUS_AND_COMMISSIONS:
        return <BonusAndCommision />;
      case ApplicationFormPageEnum.SECOND_JOB:
        return <SecondJob />;
      case ApplicationFormPageEnum.OTHER_INCOME:
        return <OtherIncome />;
      case ApplicationFormPageEnum.BENEFITS_AND_CREDITS:
        return <BenefitsAndCredits />;
      case ApplicationFormPageEnum.DEPENDANTS:
        return <Dependants />;
      case ApplicationFormPageEnum.HOUSEHOLD_SPENDINGS:
        return <HouseholdSpendings />;
      case ApplicationFormPageEnum.HOUSEHOLD_OUTGOINGS:
        return <HouseholdOutgoings />;
      case ApplicationFormPageEnum.TRAVEL_EXPENSES:
        return <TravelExpenses />;
      case ApplicationFormPageEnum.LOAN_REPAYMENTS:
        return <LoanRepayments />;
      case ApplicationFormPageEnum.STUDENT_LOANS:
      case ApplicationFormPageEnum.ADD_STUDENT_LOAN:
        return <StudentLoansWrapper />;
      case ApplicationFormPageEnum.ADDITIONAL_NOTES:
        return <AdditionalNotes />;
      case ApplicationFormPageEnum.CARDS:
      case ApplicationFormPageEnum.ADD_CARD:
        return <CardsWrapper />;
      case ApplicationFormPageEnum.REVIEW_AND_SUBMIT:
        return <ReviewAndSubmit />;
      case ApplicationFormPageEnum.IN_REVIEW:
        return <InReview />;
      default:
        return null;
    }
  };

  return (
    <div className={'mx-auto max-w-(--breakpoint-md)'}>
      {formSection && (
        <h2 className={'mb-0'}>
          <FormattedMessage
            id={`applicationForm.${formSection}.label`}
            values={{ name: activeApplicantName ? `${activeApplicantName}'s` : `Applicant ${activeApplicant}'s` }}
          />
        </h2>
      )}
      <Divider className={'my-4'} />
      <ActiveFormPage />
    </div>
  );
};
